/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 *
 * created by jpatel
 * Derived Attribute filter component
 * - allows user to create the derived attribute, and allow to provide formula
 * - sends metadata update on add/edit new attribute
 * Component will send the message which will be subscribed by the plot view to make the necessary change
 */

import React from 'react';
import CodeMirror from 'codemirror';
import '../../lib/codemirror-modes/math';
import Select from '../common/Select';
import Mathjax from '../common/Mathjax';
import { DropdownIndicator } from '../../utils/dropdown-override';
import ToolTip from '../common/ToolTip.js';

class DerivedAttributeComponent extends React.Component {

    constructor(props) {
        super(props)
        this.state = {
          formulaString: this.props.formulaString,
          attrValue: "",
          formulaValue:"",
        };
    }

    componentDidMount = () => {
       this.formulaComponent = CodeMirror.fromTextArea(
        this.refs.formulaString,
        {   // https://codemirror.net/5/doc/manual.html#option_mode
            lineWrapping: true,
            extraKeys: {"Ctrl-Space": "autocomplete","Tab": false , 'Shift-Tab': false},
            mode: "mathjs", // see codemirror-modes/math.js for definition and keyword highlighting
            screenReaderLabel:this.getLoc('Edit the formula for your new attribute'), //aria-label
            tabindex: 0,
        });
        this.formulaComponent.on('change', this.updateFormula);
        this.formulaComponent.on('blur', this.props.handleBlur);
        this.formulaComponent.on('focus', this.handleFocus);

        // Set aria-live and aria-describedby
        const editorElement = this.formulaComponent.getWrapperElement();
        editorElement.setAttribute('aria-live', 'assertive');
        editorElement.setAttribute('aria-describedby', 'errorField');
    }

    handleFocus = () => {
        const value = this.formulaComponent.getValue();
        const editorElement = this.formulaComponent.getWrapperElement();
        editorElement.setAttribute('aria-label', `Current formula: ${value}`);
    }

    // Callback for input/texarea value changes
    updateFormula = (obj) => {
        this.props.updateFormula(obj.getValue());

        this.setState({
            formulaString: obj.getValue(),
        });
    }

    /*
    changeHandler: function(event) {
        this.props.updateFormula(event.target.value);

        if(event.target.name === "formulaString") {
            this.setState({
                formulaString: event.target.value
            });
        }
    },*/

    functionSelectHandler = obj => {

        var selectedFormula = obj.value;

        //pass the id to get the help text
        this.props.updateHelp(obj.id);
        this.setState({formulaValue : obj.value });
        if( selectedFormula.length > 0 ){
            this.formulaComponent.setValue(this.formulaComponent.getValue().length > 0 ? this.formulaComponent.getValue().concat(selectedFormula) : selectedFormula);
        }

    }

    symbolButtonHandler = id => {
        let Dyn = this.props.Dyn,
            obj = this.getFunctionOptions().find(obj => obj.id === id );
        if( ! obj ) {
            // symbol is not in formula list, just insert it into formula
            obj = { id:id, value:id, label:""}
        }
        this.functionSelectHandler( obj );
    }

    attributeSelectHandler = obj => {

        var selectedAttribute = obj.value,
        regexAttrPlaceHolder = /<attribute\d{1,2}>/;

        if( selectedAttribute.length > 0 ){
            if (regexAttrPlaceHolder.test(this.formulaComponent.getValue()) == true){
                this.formulaComponent.setValue(
                    this.formulaComponent.getValue().replace(regexAttrPlaceHolder, selectedAttribute));
            } else {
                this.formulaComponent.setValue(this.formulaComponent.getValue().length > 0 ? this.formulaComponent.getValue().concat(selectedAttribute) : selectedAttribute);
            }
        }
    }

    getLoc = (str) => {
        let Dyn = this.props.Dyn;
        return Dyn.loc( str );
    }

    static functionOptions = null;

    getFunctionOptions = () => {
        let Dyn = this.props.Dyn;
        if( ! DerivedAttributeComponent.functionOptions ) {
            DerivedAttributeComponent.functionOptions = [
                { id: "select", value: "", label: this.getLoc( "Formula" ) },
                // arithmetic functions
                { id: "add", value: "<attribute1> + <attribute2>", label: this.getLoc( "Addition (+)" ) },
                { id: "sub", value: "<attribute1> - <attribute2>", label: this.getLoc( "Subtraction (-)" ) },
                { id: "mul", value: "<attribute1> * <attribute2>", label: this.getLoc( "Multiplication (*)" ) },
                { id: "div", value: "<attribute1> / <attribute2>", label: this.getLoc( "Division (/)" ) },
                { id: "abs", value: "abs(<attribute1>)", label: this.getLoc( "Absolute Value" ) },
                { id: "pow", value: "pow(<attribute1>, power)", label: this.getLoc( "Power" ) },
                { id: "sqrt", value: "sqrt(<attribute1>)", label: this.getLoc( "Square Root" ) },
                { id: "nthRoot", value: "nthRoot(<attribute1>,root)", label: this.getLoc( "Nth Root" ) },
                { id: "log10", value: "log10(<attribute1>)", label: this.getLoc( "Log 10" ) },
                { id: "log", value: "log(<attribute1>, e)", label: this.getLoc( "Natural Log" ) },
                { id: "mod", value: "mod(<attribute1>, <attribute2>)", label: this.getLoc( "Modulus" ) },
                { id: "round", value: "round(<attribute1>)", label: this.getLoc( "Round" ) },
                // logical functions
                { id: "if", value: '(<attribute1> <= 7) ? "small" : "big"', label: this.getLoc( "Logical if" ) },
                { id: "and", value: "<expression1> and <expression2>", label: this.getLoc( "Logical and" ) },
                { id: "or", value: "<expression1> or <expression2>", label: this.getLoc( "Logical or" ) },
                { id: "xor", value: "<expression1> xor <expression2>", label: this.getLoc( "Logical xor" ) },
                { id: "equal", value: "<expression1> == <expression2>", label: this.getLoc( "Logical equal" ) },
                { id: "notequal", value: "<expression1> != <expression2>", label: this.getLoc( "Logical not equal" ) },
                { id: "less", value: "<expression1> < <expression2>", label: this.getLoc( "Logical less than" ) },
                { id: "lesseq", value: "<expression1> <= <expression2>", label: this.getLoc( "Logical less than or equal" ) },
                { id: "greater", value: "<expression1> > <expression2>", label: this.getLoc( "Logical greater than" ) },
                { id: "greatereq", value: "<expression1> >= <expression2>", label: this.getLoc( "Logical greater than or equal" ) },
                // undocumented: logical not: not a or not(a)
                // aggregate stats defined by Aggregate.functions()
                { id: "mean", value: "mean(<attribute1>)", label: this.getLoc( "Mean" ) },
                { id: "median", value: "median(<attribute1>)", label: this.getLoc( "Median" ) },
                { id: "iqr", value: "iqr(<attribute1>)", label: this.getLoc( "Interquartile Range" ) },
                { id: "stdDev", value: "stdDev(<attribute1>)", label: this.getLoc( "Standard Deviation" ) },
                { id: "meanAvDev", value: "meanAvDev(<attribute1>)", label: this.getLoc( "Mean Average Deviation" ) },
                { id: "variance", value: "variance(<attribute1>)", label: this.getLoc( "Variance" ) },
                { id: "sum", value: "sum(<attribute1>)", label: this.getLoc( "Sum" ) },
                { id: "min", value: "min(<attribute1>)", label: this.getLoc( "Minimum" ) },
                { id: "max", value: "max(<attribute1>)", label: this.getLoc( "Maximum" ) },
                { id: "count", value: "count(<attribute1>)", label: this.getLoc( "Count" ) },
                // text functions
                { id: "concat", value: "concat(<expression1>, <expression2>)", label: this.getLoc( "Text concatenate" ) },
                { id: "index", value: '"category"[1:3]', label: this.getLoc( "Text extract" ) },
                { id: "length", value: 'length(<attribute1>)', label: this.getLoc( "Text length" ) },
                { id: "string", value: 'string(<attribute1>)', label: this.getLoc( "Number to text" ) },
                { id: "number", value: 'number(<attribute1>)', label: this.getLoc( "Text to number" ) },
                // random functions
                { id: "random", value: "random( -100, 100 )", label: this.getLoc( "Random number" ) },
                { id: "randomInt", value: "randomInt( 1, 7 )", label: this.getLoc( "Random integer" ) },
                { id: "pickRandom", value: 'pickRandom(["A","B","C"])', label: this.getLoc( "Random pick" ) },
                // constants
                { id: "pi", value: "pi", label: this.getLoc( "pi" ) },
                { id: "e", value: "e", label: this.getLoc( "e" ) },
                // Trigonometry
                { id: "sin", value: "sin( <attribute1> )", label: this.getLoc( "Sine" ) },
                { id: "cos", value: "cos( <attribute1> )", label: this.getLoc( "Cosine" ) },
                { id: "tan", value: "tan( <attribute1> )", label: this.getLoc( "Tangent" ) },
                // undocumented formulas:
                //      see operators in http://mathjs.org/docs/expressions/syntax.html
                //      see functions in http://mathjs.org/docs/reference/functions.html
            ];
        }
        return DerivedAttributeComponent.functionOptions;
    }

    //                <Codemirror className="formulaInput" value={this.state.formulaString} ref="formulaString" name="formulaString" onChange={this.updateFormula} options={formulaOptions} />
    render = () => {
        let Dyn = this.props.Dyn;
        var functionOptions = this.getFunctionOptions(),
            attributeOptions = [
                { value: "", label: this.getLoc("Attribute") }
            ];
        //Loop thru the attributes
        let allAttributes = Dyn.dataSet.attribs.filter(item =>item.isVisible && item.name !== 'Case');

        allAttributes.map(function(item, i) {
            attributeOptions.push({
                value: "<"+item.name+">",
                label: item.name
            });
        }.bind(this));
        return (
            <div className={"tde-derived-attribute-wrapper" + (this.props.reduceCaseCardWidth ? ' tde-px-2':' tde-px-3')}>
                <div className="tde-label tde-s1 tde-font-weight-bold tde-attr-name tde-mb-2 tde-mt-3">{ this.getLoc('Formula') }<ToolTip tip={this.getLoc('Input formula for new attribute')} Dyn={this.props.Dyn} dir="r"><i className="tde-i tde-i-help tde-ml-1"></i></ToolTip></div>
                    <div className="tde-row tde-mb-3 tde-d-flex">
                    <Select
                        Dyn={Dyn}
                        aria-label={this.getLoc('Select a formula from the dropdown and add it to the formula text area')}
                        className="tde-input tde-formula-dropdown tde-select-input tde-input-select tde-flex-1"
                        isClearable={false}
                        value={functionOptions.find(obj => obj.value === this.state.formulaValue)}
                        options={functionOptions}
                        // openMenuOnFocus
                        onChange={this.functionSelectHandler}
                        isSearchable={false}
                        tabSelectsValue={false}
                        components={{
                            Option: ({ data, innerProps, children,innerRef,isSelected,isFocused }) => (
                                <div className={'tde-select-option' + (isSelected ? ' tde-selected' :"") + (isFocused ? ' tde-focused' : '')} title={data.title} {...innerProps} ref={innerRef}>
                                    <div className="tde-s1">{children}</div>
                                </div>
                            ),
                            DropdownIndicator
                        }}
                    />
                    </div>
                    <div className="tde-row tde-mb-3 tde-d-flex">
                    <Select
                        Dyn={Dyn}
                        aria-label={this.getLoc('Select an attribute to include in your formula')}
                        className="tde-input tde-select-input tde-input-select tde-flex-1"
                        isClearable={false}
                        value={attributeOptions.find(obj => obj.value === this.state.attrValue)}
                        options={attributeOptions}
                        // openMenuOnFocus
                        onChange={this.attributeSelectHandler}
                        isSearchable={false}
                        tabSelectsValue={false}
                        components={{
                            Option: ({ data, innerProps, children,innerRef,isSelected,isFocused }) => (
                                <div className={'tde-select-option' + (isSelected ? ' tde-selected' :"") + (isFocused ? ' tde-focused' : '')} title={data.title} {...innerProps} ref={innerRef}>
                                    <div className="tde-s1"><Mathjax value={children} /></div>
                                </div>
                            ),
                            DropdownIndicator
                        }}
                    />
                </div>
                <div className="tde-formula-textarea">
                    <textarea value={ this.state.formulaString } ref="formulaString" name="formulaString" onChange={ this.updateFormula } required={true}></textarea>
                    <div className="tde-mt-3" aria-label={this.getLoc('Learn more about how to write formulas to derive and create new attributes')}>
                        <a href="https://support.tuvalabs.com/hc/en-us/articles/115004503353" target="_blank" className="tde-s1 tde-font-weight-regular tde-docs-link tde-cell-subtext" >
                        <i className="tde-i tde-i-help tde-mr-1"></i> {this.getLoc('Formula Documentation')}</a>
                    </div>
                </div>


                { this.props.parseError.length > 0 &&
                    <div className="tde-row">
                        <div id="errorField" role="alert" aria-live="polite" aria-atomic="true" className="tde-error-wrapper tde-mt-3 tde-mb-0 tde-err-msg-text-color">
                            <span className="tde-s1 tde-font-weight-regular">{ this.props.parseError }</span>
                        </div>
                    </div>
                }

            </div>
        );
    }
}

export default DerivedAttributeComponent;
